(() => {
  class ComicArchive extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (!this.querySelector('[data-archive-labels]')) {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      this.initialized = true;
      try {
        this.labels = JSON.parse(this.querySelector('[data-archive-labels]').textContent);
      } catch {
        return;
      }
      this.blogURL = new URL(this.dataset.blogUrl, location.origin);
      this.input = this.querySelector('[data-query]');
      this.form = this.querySelector('[data-search-form]');
      this.grid = this.querySelector('[data-comic-grid]');
      this.empty = this.querySelector('[data-empty]');
      this.pagination = this.querySelector('[data-pagination]');
      this.count = this.querySelector('[data-results-count]');
      this.status = this.querySelector('[data-search-status]');
      this.retry = this.querySelector('[data-retry]');
      this.results = this.querySelector('[data-results]');
      this.clear = this.querySelector('[data-clear]');
      this.topics = Array.from(this.querySelectorAll('[data-topic]'));
      this.originalGrid = Array.from(this.grid.childNodes, node => node.cloneNode(true));
      this.originalPagination = Array.from(this.pagination.childNodes, node => node.cloneNode(true));
      this.originalPaginationHidden = this.pagination.hidden;
      this.originalEmptyHidden = this.empty.hidden;
      this.originalCount = this.count.textContent;
      this.sequence = 0;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form.hidden = false;
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        const query = this.input.value.trim().slice(0, 160);
        const url = this.makeURL({ query, page: 1 });
        if (!query) {
          location.assign(url);
          return;
        }
        history.pushState({}, '', url);
        this.search(true);
      }, options);
      this.addEventListener('click', event => {
        const topic = event.target.closest('[data-topic]');
        const page = event.target.closest('[data-search-page]');
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (topic && this.query()) {
          event.preventDefault();
          const url = this.makeURL({ path: new URL(topic.href).pathname, page: 1 });
          history.pushState({}, '', url);
          this.search(true);
        } else if (page) {
          event.preventDefault();
          history.pushState({}, '', page.href);
          this.search(true, true);
        }
      }, options);
      this.retry.addEventListener('click', () => this.search(true), options);
      window.addEventListener('popstate', () => {
        if (this.query()) this.search(false);
        else location.reload();
      }, options);
      this.refreshLinks();
      if (this.query()) this.search(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.fetchController?.abort();
      this.initialized = false;
    }

    query() {
      return (new URL(location.href).searchParams.get('q') || '').trim().slice(0, 160);
    }

    topic() {
      return this.topics.find(link => new URL(link.href).pathname === location.pathname)?.dataset.topic ?? this.dataset.currentTopic ?? '';
    }

    makeURL({ query = this.query(), page = 1, path = location.pathname } = {}) {
      const url = new URL(location.href);
      url.pathname = path;
      url.hash = '';
      url.searchParams.delete('q');
      url.searchParams.delete('page');
      url.searchParams.delete('comic_page');
      if (query) {
        url.searchParams.set('q', query);
        if (page > 1) url.searchParams.set('comic_page', page);
      } else if (page > 1) {
        url.searchParams.set('page', page);
      }
      return url;
    }

    format(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`__${name}__`, String(value)), this.labels[key] || '');
    }

    refreshLinks() {
      const query = this.query();
      this.input.value = query;
      this.topics.forEach(link => {
        const path = new URL(link.href).pathname;
        link.href = this.makeURL({ path });
        if (path === location.pathname) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      this.clear.hidden = !query && location.pathname === this.blogURL.pathname;
      const from = location.pathname + location.search;
      this.querySelectorAll('[data-comic-link]').forEach(link => {
        const url = new URL(link.href);
        url.searchParams.set('from', from);
        link.href = url;
      });
    }

    async loadCatalog() {
      if (this.catalog) return this.catalog;
      if (this.catalogPromise) return this.catalogPromise;
      this.catalogPromise = (async () => {
        const entries = new Map();
        const seen = new Set();
        let total;
        let url = new URL(this.blogURL);
        url.searchParams.set('view', 'comic-index');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        while (url) {
          if (seen.has(url.href) || seen.size >= 500) throw new Error('Incomplete comic index');
          seen.add(url.href);
          this.fetchController = new AbortController();
          const timer = window.setTimeout(() => this.fetchController.abort(), 15000);
          let data;
          try {
            const response = await fetch(url, { credentials: 'same-origin', signal: this.fetchController.signal });
            if (!response.ok) throw new Error('Comic index unavailable');
            data = await response.json();
          } finally {
            window.clearTimeout(timer);
          }
          if (data.version !== 1 || !Array.isArray(data.articles) || !Number.isInteger(data.total)) throw new Error('Invalid comic index');
          total = data.total;
          data.articles.forEach(item => {
            if (!item || typeof item.title !== 'string' || typeof item.url !== 'string' || !Array.isArray(item.tags)) throw new Error('Invalid comic record');
            const articleURL = new URL(item.url, location.origin);
            if (articleURL.origin !== location.origin || !articleURL.pathname.startsWith(this.blogURL.pathname + '/')) throw new Error('Invalid comic URL');
            entries.set(String(item.id), item);
          });
          if (data.next) {
            const next = new URL(data.next, url);
            if (next.origin !== location.origin || next.pathname !== this.blogURL.pathname) throw new Error('Invalid comic pagination');
            next.searchParams.set('view', 'comic-index');
            if (preview) next.searchParams.set('preview_theme_id', preview);
            url = next;
          } else url = null;
        }
        if (entries.size !== total) throw new Error('Incomplete comic index');
        this.catalog = Array.from(entries.values());
        return this.catalog;
      })();
      try {
        return await this.catalogPromise;
      } finally {
        this.catalogPromise = null;
      }
    }

    async search(focusResults, scrollResults = false) {
      const sequence = ++this.sequence;
      const query = this.query();
      const topic = this.topic();
      this.refreshLinks();
      this.results.setAttribute('aria-busy', 'true');
      this.status.hidden = false;
      this.status.textContent = this.labels.loading;
      this.retry.hidden = true;
      try {
        const catalog = await this.loadCatalog();
        if (sequence !== this.sequence || !this.isConnected) return;
        const normalize = text => text.normalize('NFKC').toLocaleLowerCase();
        const terms = normalize(query).split(/\s+/).filter(Boolean);
        const found = catalog.filter(item => {
          if (topic && !item.tags.includes(topic)) return false;
          const searchable = normalize([item.title, item.excerpt, ...item.tags].join(' '));
          return terms.every(term => searchable.includes(term));
        });
        const totalPages = Math.max(1, Math.ceil(found.length / 12));
        const requestedPage = parseInt(new URL(location.href).searchParams.get('comic_page'), 10) || 1;
        const page = Math.min(totalPages, Math.max(1, requestedPage));
        if (page !== requestedPage) history.replaceState({}, '', this.makeURL({ page }));
        const start = (page - 1) * 12;
        this.grid.replaceChildren(...found.slice(start, start + 12).map((item, index) => this.card(item, index < 3, index === 0)));
        this.empty.hidden = found.length > 0;
        this.count.textContent = found.length ? this.format(found.length === 1 ? 'rangeOne' : 'rangeOther', { start: start + 1, end: Math.min(start + 12, found.length), count: found.length }) : this.labels.noResults;
        this.renderPagination(page, totalPages);
        this.status.hidden = true;
        this.refreshLinks();
        if (focusResults) {
          this.count.tabIndex = -1;
          this.count.focus({ preventScroll: true });
        }
        if (scrollResults) this.results.scrollIntoView({ behavior: 'instant', block: 'start' });
      } catch {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.grid.replaceChildren(...this.originalGrid.map(node => node.cloneNode(true)));
        this.pagination.replaceChildren(...this.originalPagination.map(node => node.cloneNode(true)));
        this.pagination.hidden = this.originalPaginationHidden;
        this.empty.hidden = this.originalEmptyHidden;
        this.count.textContent = this.originalCount;
        this.status.textContent = this.labels.error;
        this.status.hidden = false;
        this.retry.hidden = false;
        this.refreshLinks();
      } finally {
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    card(item, eager, priority) {
      const card = this.element('article', 'comic-card');
      const link = this.element('a', 'comic-card__link');
      link.href = item.url;
      link.setAttribute('data-comic-link', '');
      const cover = this.element('div', 'comic-card__cover');
      let imageURL;
      try {
        const candidate = new URL(item.image, location.href);
        if (item.image && ['https:', 'http:'].includes(candidate.protocol)) imageURL = candidate;
      } catch {}
      if (imageURL) {
        const image = this.element('img');
        image.src = imageURL;
        image.alt = '';
        image.width = Number(item.width) || 1080;
        image.height = Number(item.height) || 1440;
        image.loading = eager ? 'eager' : 'lazy';
        image.decoding = 'async';
        if (priority) image.fetchPriority = 'high';
        if (item.srcset) {
          image.srcset = item.srcset;
          image.sizes = '(min-width: 1280px) 397px, (min-width: 800px) 31vw, (min-width: 380px) 45vw, 260px';
        }
        cover.append(image);
      } else cover.append(this.element('span', 'comic-card__cover-title', item.title));
      const body = this.element('div', 'comic-card__body');
      const metadata = [item.topic];
      if (item.pages > 0) metadata.push(this.format(item.pages === 1 ? 'pageOne' : 'pageOther', { count: item.pages }));
      if (metadata.filter(Boolean).length) body.append(this.element('p', 'comic-card__meta', metadata.filter(Boolean).join(' · ')));
      body.append(this.element('h2', 'comic-card__title', item.title));
      if (item.excerpt) body.append(this.element('p', 'comic-card__summary', item.excerpt));
      const read = this.element('span', 'comic-card__read', this.labels.read);
      const arrow = this.element('span', '', '→');
      arrow.setAttribute('aria-hidden', 'true');
      read.append(arrow);
      body.append(read);
      link.append(cover, body);
      card.append(link);
      return card;
    }

    renderPagination(page, totalPages) {
      this.pagination.replaceChildren();
      this.pagination.hidden = totalPages <= 1;
      if (totalPages <= 1) return;
      const addLink = (number, text, label, direction = false) => {
        const link = this.element('a', direction ? 'comic-archive__page-direction' : '', text);
        link.href = this.makeURL({ page: number });
        link.setAttribute('aria-label', label);
        link.setAttribute('data-search-page', '');
        this.pagination.append(link);
      };
      if (page > 1) addLink(page - 1, `← ${this.labels.previous}`, this.labels.previousPage, true);
      const numbers = [...new Set([1, page - 1, page, page + 1, totalPages].filter(number => number > 0 && number <= totalPages))].sort((a, b) => a - b);
      let previous = 0;
      numbers.forEach(number => {
        if (previous && number - previous > 1) this.pagination.append(this.element('span', 'comic-archive__ellipsis', '…'));
        if (number === page) {
          const current = this.element('span', '', number);
          current.setAttribute('aria-current', 'page');
          current.setAttribute('aria-label', this.format('pageLabel', { page: number }));
          this.pagination.append(current);
        } else addLink(number, number, this.format('pageLabel', { page: number }));
        previous = number;
      });
      if (page < totalPages) addLink(page + 1, `${this.labels.next} →`, this.labels.nextPage, true);
    }
  }
  if (!customElements.get('comic-archive')) customElements.define('comic-archive', ComicArchive);
})();
